import { create } from 'zustand';
import { AtlasClient } from '../api/atlasClient';
import { Dimension, PrismElement, AtlasArticle, ArticleVersion, AtlasContext, AtlasPosthocTagMap } from '../types/atlas';
import { getContextKey } from '../utils/context';
import { SparkProjectOverview, SparkProjectDetails, SparkCreative, SparkIteration, SparkJob, SparkFlow, SparkSuggestedResource, SparkCreativeType, SparkTagDimensionSelection, TagFacetDimension, SparkFormDefinition, SparkFormValues, SparkFormValidationIssue, SparkLibraryItem, SparkLibrarySearchResponse, FocusSearchQuestion } from '../features/spark/types/sparkTypes';
import { buildSparkFormDefinitions } from '../features/spark/utils/forms';
import { User } from '../types/user';

const client = new AtlasClient();

export type ProfileViewMode = 'findings' | 'summary';

// DRY helper: synthesize per-form values and issues from a flat iteration and a creative's overlay
function synthesizePerFormFromOverlay(
  creative: SparkCreative | undefined,
  iteration: SparkIteration
): { valuesByForm: Record<string, SparkFormValues> | null; issuesByForm: Record<string, SparkFormValidationIssue[]> | null } {
  const overlay = (creative as any)?.forms_overlay as { id: string; groups: { elements: string[] }[] }[] | undefined;
  if (!overlay) {
    return { valuesByForm: null, issuesByForm: null };
  }
  const valuesByForm: Record<string, SparkFormValues> = {};
  const issuesByForm: Record<string, SparkFormValidationIssue[]> = {};
  overlay.forEach((spec) => {
    const fid = spec.id;
    const elementIds = (spec.groups || []).flatMap(g => (g.elements || []));
    const vals: Record<string, any> = {};
    elementIds.forEach(eid => { if ((iteration as any).values && (iteration as any).values[eid] !== undefined) vals[eid] = (iteration as any).values[eid]; });
    valuesByForm[fid] = vals;
    const issues: SparkFormValidationIssue[] = [];
    elementIds.forEach(eid => {
      const arr = (iteration as any).validation_issues?.[eid] || [];
      issues.push(...arr);
    });
    issuesByForm[fid] = issues;
  });
  return { valuesByForm, issuesByForm };
}

function resolveCreativeSummaryFromIteration(creative: SparkCreative, iteration: SparkIteration): string {
  const summaryElementId = creative.summary_element_id;
  if (summaryElementId) {
    const raw = iteration.values?.[summaryElementId];
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (trimmed) {
        return trimmed;
      }
    }
  }
  return iteration.description ?? '';
}

function resolveCreativeHeroImageFromIteration(
  creative: SparkCreative,
  iteration: SparkIteration
): string | null | undefined {
  const heroImageElementId = creative.hero_image_element_id;
  if (!heroImageElementId) {
    return creative._hero_image_id;
  }
  const currentFileIds = iteration.images?.[heroImageElementId]?.current_file_ids;
  return Array.isArray(currentFileIds) && currentFileIds.length > 0 ? currentFileIds[0] : null;
}

function mergeActiveIterationIntoCreative(creative: SparkCreative, iteration: SparkIteration): SparkCreative {
  const activeIterationFqn =
    creative._active_iteration_fqn ||
    (creative._iteration_fqns.length > 0 ? creative._iteration_fqns[creative._iteration_fqns.length - 1] : null);

  if (creative.fqn !== iteration._creative_fqn || activeIterationFqn !== iteration.fqn) {
    return creative;
  }

  const nextName = iteration.name || creative.name;
  const nextDescription = resolveCreativeSummaryFromIteration(creative, iteration);
  const nextHeroImageId = resolveCreativeHeroImageFromIteration(creative, iteration);

  if (
    nextName === creative.name &&
    nextDescription === (creative.description ?? '') &&
    nextHeroImageId === creative._hero_image_id
  ) {
    return creative;
  }

  return {
    ...creative,
    name: nextName,
    description: nextDescription,
    _hero_image_id: nextHeroImageId,
  };
}

export interface AtlasUpdate {
  type: 'NEW_ELEMENT' | 'NEW_ARTICLE' | 'ARTICLE_UPDATE';
  subject_fqn: string;
  context: AtlasContext;
  title: string;
  description: string;
  image_id?: string;
  resource_fqn: string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH';
  priority_reasoning?: string;
  date: string;
}

export interface AtlasItem {
  type: 'ELEMENT' | 'PROFILE';
  subject_fqn: string;
  context: AtlasContext;
  title: string;
  description: string;
  image_id?: string;
  resource_fqn?: string;
  date: string;
}

export interface AtlasState {
  // Updates State
  updates: AtlasUpdate[];
  isLoading: {
    dimensions: boolean;
    profiles: boolean;
    updates: boolean;
    itemElements: boolean;
  };
  error: string | null;
  currentPage: number;
  hasMoreUpdates: boolean;
  dimensionFqn: string | null;

  // Items State
  currentItemsDimension: string | null;
  itemElements: PrismElement[];
  availableContexts: Array<{ context: AtlasContext; count: number }>;
  selectedContext: AtlasContext | null;
  elementProfileMap: Map<string, Array<{ fqn: string; context: AtlasContext; date?: string }>>;
  dimensionArticles: Array<{
    fqn: string;
    name: string;
    subject_fqn: string;
    summary: string;
    hero_image_id?: string;
    context?: AtlasContext;
    latest_version_posthoc_tags?: AtlasPosthocTagMap;
    latest_version_posthoc_related_tags?: AtlasPosthocTagMap;
    date?: string;
  }>;

  // Profile State
  dimensions: Dimension[];
  selectedProfile: AtlasArticle | null;
  selectedVersion: ArticleVersion | null;
  profiles: AtlasArticle[];
  viewMode: ProfileViewMode;
  elements: PrismElement[];
  isInitializingFromUrl: boolean;

  // Updates Actions
  fetchUpdates: (page: number, dimensionFqn?: string) => Promise<void>;
  loadMoreUpdates: () => Promise<void>;

  // Items Actions
  setCurrentItemsDimension: (dimensionFqn: string | null) => void;
  fetchItemElements: (dimensionFqn: string) => Promise<void>;
  setSelectedContext: (context: AtlasContext | null) => void;

  // Profile Actions
  fetchDimensions: () => Promise<void>;
  setViewMode: (mode: ProfileViewMode) => void;
  setSelectedProfile: (profile: AtlasArticle | null) => void;
  setElements: (elements: PrismElement[]) => void;
  setError: (error: unknown) => void;
  setIsInitializingFromUrl: (value: boolean) => void;
  fetchProfileByFqn: (profileFqn: string, timestamp?: string) => Promise<void>;
  listArticlesByTarget: (targetFqn: string) => Promise<{ fqn: string; context: AtlasContext }[]>;

  // Spark State
  sparkProjects: SparkProjectOverview[];
  currentSparkProject: SparkProjectDetails | null;
  currentSparkFlow: SparkFlow | null;
  currentSparkCreatives: SparkCreative[];
  currentSparkJobs: SparkJob[];
  // Global Spark status (one-shot refactor additions)
  lastActiveSparkProjectFqn: string | null;
  workerIsOnline: boolean;
  currentSparkProjectTotalCompletedCount: number | null;
  sparkCreativeTypes: SparkCreativeType[];
  sparkFormDefinitions: Record<string, Record<string, SparkFormDefinition> | null>; // creativeFqn -> { formId: def }
  sparkIterationFormValues: Record<string, Record<string, SparkFormValues> | null>; // iterationFqn -> { formId: values }
  sparkIterationValidationIssues: Record<string, Record<string, SparkFormValidationIssue[]> | null>;
  isLoadingSpark: {
    projects: boolean;
    projectDetails: boolean;
    creatives: boolean;
    jobs: boolean;
    action: boolean;
    uploadResource: boolean;
    removeResource: boolean;
    addMember: boolean;
    removeMember: boolean;
    suggestSparkDeepResearchBriefing: boolean;
    runSparkDeepResearch: boolean;
    generateFSQ: boolean;
    runFSQResearch: boolean;
    suggestResources: boolean;
    addResource: boolean;
    cancelJob: boolean;
    creativeTypes: boolean;
  };
  sparkError: string | null;

  // Spark State Flags for UI Interaction Control
  isUserInteractionBlockingRefresh: boolean;
  pendingProjectRefreshFqn: string | null;

  // Spark Actions
  fetchSparkProjects: (args?: { background?: boolean }) => Promise<void>;
  fetchSparkProjectDetails: (projectFqn: string) => Promise<void>;
  createSparkProject: (flowFqn: string, projectName: string) => Promise<string | null>;
  deleteSparkProject: (projectFqn: string) => Promise<boolean>;
  closeSparkProject: (projectFqn: string) => Promise<void>;
  reopenSparkProject: (projectFqn: string) => Promise<void>;
  updateSparkProjectOverview: (projectFqn: string, data: { name?: string; briefing?: string; briefing_session_id?: string; tag_pattern?: SparkTagDimensionSelection[] }) => Promise<void>;
  uploadSparkResource: (projectFqn: string, file: File) => Promise<void>;
  suggestSparkDeepResearchBriefing: (projectFqn: string) => Promise<string | null>;
  runSparkDeepResearch: (projectFqn: string, briefing: string, webSearch: boolean) => Promise<void>;
  generateFSQ: (projectFqn: string) => Promise<FocusSearchQuestion[]>;
  runFSQResearch: (projectFqn: string, fsqs: FocusSearchQuestion[], webSearch: boolean) => Promise<number>;
  suggestSparkResources: (projectFqn: string) => Promise<{ suggested_resources: SparkSuggestedResource[] }>;
  addSparkResource: (projectFqn: string, resourceFqn: string, comment: string | null) => Promise<void>;
  removeSparkResource: (projectFqn: string, identifiers: { resourceFqn: string | null, jobFqn: string | null }) => Promise<void>;
  addSparkLabel: (creativeFqns: string[], label: string) => Promise<void>;
  removeSparkLabel: (creativeFqns: string[], label: string) => Promise<void>;
  updateSparkCreative: (creativeFqn: string, data: { name?: string; description?: string | null; tag_pattern?: SparkTagDimensionSelection[] }) => Promise<void>;
  deleteSparkCreatives: (creativeFqns: string[]) => Promise<void>;
  updateSparkIteration: (iterationFqn: string, data: any) => Promise<SparkIteration>;
  addSparkFeedback: (iterationFqn: string, content: string, metadata?: Record<string, any>, isComment?: boolean) => Promise<void>;
  updateSparkFeedback: (iterationFqn: string, feedbackId: string, content: string) => Promise<void>;
  deleteSparkFeedback: (iterationFqn: string, feedbackId: string) => Promise<void>;
  selectSparkCurrentImage: (iterationFqn: string, elementId: string, fileIds: string[]) => Promise<SparkIteration>;
  deleteSparkImageAlternative: (iterationFqn: string, elementId: string, fileId: string) => Promise<SparkIteration>;
  uploadSparkImageAlternative: (iterationFqn: string, elementId: string, file: File) => Promise<SparkIteration>;
  triggerSparkAction: (
    target: { projectFqn?: string | null; libraryItemFqn?: string | null },
    actionName: string,
    actionArgs: any,
    options?: { sourceTypeFqn?: string | null; targetTypeFqn?: string | null }
  ) => Promise<void>;
  // Legacy published flow removed; replaced by Library Items
  markSparkJobAsSeen: (jobFqns: string[]) => Promise<boolean>;
  downloadSparkIterationExport: (iterationFqn: string, format: 'pptx' | 'docx') => Promise<void>;
  fetchSparkFlows: () => Promise<SparkFlow[]>;
  fetchSparkJobs: (projectFqn: string) => Promise<void>;
  cancelSparkJob: (jobFqn: string) => Promise<boolean>;
  fetchSparkCreativeTypes: () => Promise<void>;
  cacheSparkIterations: (iterations: SparkIteration | SparkIteration[]) => void;
  setLastActiveSparkProject: (fqn: string) => void;
  setPendingProjectRefresh: (fqn: string) => void;
  clearPendingProjectRefresh: () => void;
  setWorkerOnline: (online: boolean) => void;
  setTotalCompletedForCurrentProject: (count: number) => void;

  // (Legacy published) Removed; use library items instead

  // Library Items (Published Items Redesign)
  librarySearchResults: SparkLibrarySearchResponse | null;
  libraryFacets: TagFacetDimension[];
  libraryIncludeDrafts: boolean;
  isLoadingLibrary: { search: boolean; facets: boolean };
  libraryError: string | null;
  searchLibraryItems: (params: { q?: string; tags?: string[]; page?: number; page_size?: number; sort?: 'name' | 'publishedAt'; dir?: 'asc' | 'desc'; creative_type_fqn?: string; include_drafts?: boolean; state?: 'draft' | 'published'; owner?: string }) => Promise<SparkLibrarySearchResponse>;
  fetchLibraryFacets: (params: { q?: string; tags?: string[]; creative_type_fqn?: string; include_drafts?: boolean; state?: 'draft' | 'published'; owner?: string }) => Promise<{ dimensions: TagFacetDimension[] }>;
  loadLibrarySearch: (params: { q?: string; tags?: string[]; page?: number; page_size?: number; sort?: 'name' | 'publishedAt'; dir?: 'asc' | 'desc'; creative_type_fqn?: string; include_drafts?: boolean; state?: 'draft' | 'published'; owner?: string }) => Promise<void>;
  loadLibraryFacets: (params: { q?: string; tags?: string[]; creative_type_fqn?: string; include_drafts?: boolean; state?: 'draft' | 'published'; owner?: string }) => Promise<void>;
  setLibraryIncludeDrafts: (include: boolean) => void;
  getLibraryItem: (itemFqn: string) => Promise<SparkLibraryItem>;
  createLibraryItemFromIteration: (iterationFqn: string) => Promise<SparkLibraryItem>;
  publishLibraryItem: (itemFqn: string) => Promise<SparkLibraryItem>;
  unpublishLibraryItem: (itemFqn: string) => Promise<SparkLibraryItem>;
  updateLibraryItem: (itemFqn: string, payload: Partial<Pick<SparkLibraryItem, 'title' | 'description' | 'labels' | 'values'>> & { tag_pattern?: any[] }) => Promise<SparkLibraryItem>;
  getLibraryHtmlUrl: (itemFqn: string) => string;
  getLibraryPptxUrl: (itemFqn: string) => string;
  getLibraryExportAssetUrl: (itemFqn: string, exportId: string, filename: string) => string;
  uploadLibraryItemImageAlternative: (itemFqn: string, elementId: string, file: File) => Promise<SparkLibraryItem>;
  selectLibraryItemCurrentImage: (itemFqn: string, data: { element_id: string; file_ids: string[] }) => Promise<SparkLibraryItem>;
  deleteLibraryItemImageAlternative: (itemFqn: string, data: { element_id: string; file_id: string }) => Promise<SparkLibraryItem>;
  setLibraryItemImageComment: (itemFqn: string, elementId: string, fileId: string, comment: string) => Promise<SparkLibraryItem>;
  listLibraryItemAttachments: (itemFqn: string) => Promise<{ file_id: string; filename: string; mime_type: string; size: number; uploaded_by?: string; uploaded_at?: string }[]>;
  uploadLibraryItemAttachment: (itemFqn: string, file: File) => Promise<{ file_id: string; filename: string; mime_type: string; size: number }>;
  deleteLibraryItemAttachment: (itemFqn: string, fileId: string) => Promise<{ file_id: string }>;
  getLibraryItemAttachmentDownloadUrl: (itemFqn: string, fileId: string) => string;
  deleteLibraryItem: (itemFqn: string) => Promise<{ success: boolean }>;

  // User State & Actions
  tenantUsers: User[];
  isLoadingTenantUsers: boolean;
  fetchTenantUsers: (force?: boolean) => Promise<void>;
  addUserToSparkProject: (projectFqn: string, username: string) => Promise<void>;
  removeUserFromSparkProject: (projectFqn: string, username: string) => Promise<void>;
  setSparkUserInteractionBlockingRefresh: (isBlocking: boolean) => void;
  resetSparkState: () => void;
}

export const useAtlasStore = create<AtlasState>((set, get) => ({
  // Updates Initial State
  updates: [],
  error: null,
  currentPage: 0,
  hasMoreUpdates: true,
  dimensionFqn: null,

  // Items Initial State
  currentItemsDimension: null,
  itemElements: [],
  availableContexts: [],
  selectedContext: null,
  elementProfileMap: new Map(),
  dimensionArticles: [],

  // Library initial state
  librarySearchResults: null,
  libraryFacets: [],
  libraryIncludeDrafts: false,
  isLoadingLibrary: { search: false, facets: false },
  libraryError: null,

  // Profile Initial State
  dimensions: [],
  selectedProfile: null,
  profiles: [],
  viewMode: 'summary',
  elements: [],
  isInitializingFromUrl: false,
  selectedVersion: null,
  isLoading: {
    dimensions: false,
    profiles: false,
    updates: false,
    itemElements: false
  },

  // Updates Actions
  fetchUpdates: async (page: number = 0, dimensionFqn?: string) => {
    set(state => ({
      isLoading: { ...state.isLoading, updates: true },
      error: null,
      dimensionFqn: page === 0 ? dimensionFqn || null : state.dimensionFqn
    }));
    try {
      // Pass parameters using correct names (dimension_fqn, page_number)
      const currentDimensionFqn = page === 0 ? dimensionFqn : get().dimensionFqn;
      const updates = await client.getUpdates(currentDimensionFqn || undefined, page);
      set(state => ({
        updates: page === 0 ? updates : [...state.updates, ...updates],
        currentPage: page,
        hasMoreUpdates: updates.length === 25 // Server page size is 25
      }));
    } catch (error: unknown) {
      console.error('Failed to fetch updates:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch updates';
      set({ error: errorMessage });
    } finally {
      set(state => ({ isLoading: { ...state.isLoading, updates: false } }));
    }
  },

  loadMoreUpdates: async () => {
    const { currentPage, hasMoreUpdates } = get();
    if (!hasMoreUpdates) return;

    await get().fetchUpdates(currentPage + 1);
  },

  // Items Actions
  setCurrentItemsDimension: (dimensionFqn: string | null) => {
    set({
      currentItemsDimension: dimensionFqn,
      selectedContext: null, // Reset context when changing dimensions
      availableContexts: [],  // Clear available contexts
      elementProfileMap: new Map(), // Clear element profile map
      dimensionArticles: [], // Clear dimension articles
    });
    if (dimensionFqn) {
      get().fetchItemElements(dimensionFqn);
    }
  },

  fetchItemElements: async (dimensionFqn: string) => {
    set(state => ({ isLoading: { ...state.isLoading, itemElements: true }, error: null }));
    try {
      // Fetch all dimensions and elements, then filter
      const allDimensions = await client.getDimensionsAndElements();
      const targetDimension = allDimensions.find(dim => dim.fqn === dimensionFqn);
      const elements = targetDimension ? targetDimension.elements : [];

      // Keep ALL elements (active and inactive) - let the UI handle filtering

      // Fetch articles for this dimension and merge lightweight post-hoc tag metadata.
      const [articles, articlePosthocTagsResult] = await Promise.all([
        client.listArticlesByDimension(dimensionFqn),
        client.listArticlePosthocTagsByDimension(dimensionFqn).catch((error) => {
          console.warn('Failed to fetch article post-hoc tag summaries:', error);
          return [];
        }),
      ]);
      const posthocTagsByArticleFqn = new Map(
        articlePosthocTagsResult.map((article) => [
          article.fqn,
          {
            latest_version_posthoc_tags: article.latest_version_posthoc_tags,
            latest_version_posthoc_related_tags: article.latest_version_posthoc_related_tags,
          },
        ])
      );
      const mergedArticles = articles.map((article) => ({
        ...article,
        ...(posthocTagsByArticleFqn.get(article.fqn) || {}),
      }));

      // Extract and group unique contexts
      const contextMap = new Map<string, { context: AtlasContext; count: number }>();
      mergedArticles.forEach(article => {
        const contextKey = getContextKey(article.context || null);
        if (contextMap.has(contextKey)) {
          contextMap.get(contextKey)!.count++;
        } else {
          contextMap.set(contextKey, { context: article.context || {}, count: 1 });
        }
      });

      const availableContexts = Array.from(contextMap.values())
        .sort((a, b) => b.count - a.count); // Sort by count, most common first

      // Get current state to check selectedContext
      const currentState = get();
      let selectedContext = currentState.selectedContext;

      // Auto-select first context if none selected and contexts are available
      if (!selectedContext && availableContexts.length > 0) {
        selectedContext = availableContexts[0].context;
      }

      // Store ALL elements (active and inactive) - filtering now handled by UI
      set({
        itemElements: elements,
        availableContexts,
        selectedContext,
        dimensionArticles: mergedArticles,
        error: null
      });

      // Create elementProfileMap: Map element FQN to its articles
      const profileMap = new Map<string, Array<{ fqn: string; context: AtlasContext; date?: string }>>();
      mergedArticles.forEach(article => {
        if (!profileMap.has(article.subject_fqn)) {
          profileMap.set(article.subject_fqn, []);
        }
        profileMap.get(article.subject_fqn)!.push({ fqn: article.fqn, context: article.context || {}, date: article.date });
      });

      set({ elementProfileMap: profileMap });
    } catch (error: unknown) {
      console.error('Failed to fetch elements:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch elements';
      set({ error: errorMessage });
    } finally {
      set(state => ({ isLoading: { ...state.isLoading, itemElements: false } }));
    }
  },

  setSelectedContext: (context: AtlasContext | null) => {
    set({ selectedContext: context });
  },

  // Profile Actions
  fetchDimensions: async () => {
    set(state => ({ isLoading: { ...state.isLoading, dimensions: true } }));
    try {
      // Fetch dimensions and their nested elements together
      const dimensionsWithElements = await client.getDimensionsAndElements();
      set({ dimensions: dimensionsWithElements });
    } catch (error) {
      console.error('Failed to fetch dimensions:', error);
    } finally {
      set(state => ({ isLoading: { ...state.isLoading, dimensions: false } }));
    }
  },

  setViewMode: (mode: ProfileViewMode) => {
    set({ viewMode: mode });
  },

  setSelectedProfile: (profile) => set({ selectedProfile: profile }),

  setElements: (elements) => set({ elements }),

  setError: (error: unknown) => {
    const errorMessage = error instanceof Error ? error.message :
      typeof error === 'string' ? error : 'An unexpected error occurred';
    set({ error: errorMessage });
  },

  setIsInitializingFromUrl: (value: boolean) => set({ isInitializingFromUrl: value }),

  fetchProfileByFqn: async (profileFqn: string, timestamp?: string) => {
    set(state => ({ isLoading: { ...state.isLoading, profiles: true }, error: null }));
    try {
      const profile = await client.getProfileByFqn(profileFqn);

      // Get the dimension FQN from the profile subject
      const dimensionFqn = `prism_dimension@${profile.subject.fqn.split('@')[1].split('/')[0]}`;
      await get().fetchItemElements(dimensionFqn);

      // Sort versions by date in descending order (newest first)
      const sortedVersions = [...profile.versions].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );

      // If a timestamp is provided, find the most recent version before that timestamp
      if (timestamp && sortedVersions.length > 0) {
        const targetDate = new Date(timestamp).getTime();

        // Find the first version that's before or at the target date
        const selectedVersion = sortedVersions.find(version =>
          new Date(version.date).getTime() <= targetDate
        );

        if (selectedVersion) {
          set({
            selectedProfile: profile,
            selectedVersion: selectedVersion
          });
          return;
        }
      }

      // If no timestamp provided or no version found before timestamp, use latest version
      set({
        selectedProfile: profile,
        selectedVersion: sortedVersions[0]
      });

    } catch (error: unknown) {
      console.error('Failed to fetch profile:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch profile';
      set({ error: errorMessage });
    } finally {
      set(state => ({ isLoading: { ...state.isLoading, profiles: false } }));
    }
  },

  listArticlesByTarget: async (targetFqn: string) => {
    try {
      const articles = await client.listArticlesBySubject(targetFqn);
      return articles;
    } catch (error) {
      console.error('Failed to fetch available profiles/articles:', error);
      throw error;
    }
  },

  // Spark Initial State
  sparkProjects: [],
  currentSparkProject: null,
  currentSparkFlow: null,
  currentSparkCreatives: [],
  currentSparkJobs: [],
  lastActiveSparkProjectFqn: null,
  workerIsOnline: true,
  currentSparkProjectTotalCompletedCount: null,
  sparkCreativeTypes: [],
  sparkFormDefinitions: {},
  sparkIterationFormValues: {},
  sparkIterationValidationIssues: {},
  isLoadingSpark: {
    projects: false,
    projectDetails: false,
    creatives: false,
    jobs: false,
    action: false,
    uploadResource: false,
    removeResource: false,
    addMember: false,
    removeMember: false,
    suggestSparkDeepResearchBriefing: false,
    runSparkDeepResearch: false,
    generateFSQ: false,
    runFSQResearch: false,
    suggestResources: false,
    addResource: false,
    cancelJob: false,
    creativeTypes: false,
  },
  sparkError: null,

  // Spark Initial State Flags
  isUserInteractionBlockingRefresh: false,
  pendingProjectRefreshFqn: null,

  // Spark Actions (Implementations)
  fetchSparkProjects: async (args?: { background?: boolean }) => {
    const background = !!args?.background;
    if (!background) {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, projects: true }, sparkError: null }));
    } else {
      set(state => ({ sparkError: null }));
    }
    try {
      const projects = await client.getSparkProjects(); // Use client
      set({ sparkProjects: projects || [] }); // Handle potential undefined
    } catch (error: any) {
      set({ sparkError: error.message || 'Failed to fetch Spark projects' });
    } finally {
      if (!background) {
        set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, projects: false } }));
      }
    }
  },

  setLastActiveSparkProject: (fqn: string) => {
    set((state) => (state.lastActiveSparkProjectFqn === fqn ? state : { lastActiveSparkProjectFqn: fqn }));
  },

  setPendingProjectRefresh: (fqn: string) => {
    set({ pendingProjectRefreshFqn: fqn });
  },

  clearPendingProjectRefresh: () => {
    set({ pendingProjectRefreshFqn: null });
  },

  setWorkerOnline: (online: boolean) => {
    set((state) => (state.workerIsOnline === online ? state : { workerIsOnline: online }));
  },

  setTotalCompletedForCurrentProject: (count: number) => {
    set((state) => (state.currentSparkProjectTotalCompletedCount === count ? state : { currentSparkProjectTotalCompletedCount: count }));
  },

  closeSparkProject: async (projectFqn: string): Promise<void> => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      const updated = await client.closeSparkProject(projectFqn);
      // Update current project if matches
      set(state => ({
        currentSparkProject: state.currentSparkProject?.fqn === projectFqn ? updated : state.currentSparkProject,
        // Update project list item if present
        sparkProjects: state.sparkProjects.map(p => p.fqn === projectFqn ? { ...p, is_closed: true, status: 'CLOSED' } : p),
      }));
    } catch (error: any) {
      const message = error.response?.data?.error || (error instanceof Error ? error.message : 'Failed to close project');
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },

  reopenSparkProject: async (projectFqn: string): Promise<void> => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      const updated = await client.reopenSparkProject(projectFqn);
      set(state => ({
        currentSparkProject: state.currentSparkProject?.fqn === projectFqn ? updated : state.currentSparkProject,
        sparkProjects: state.sparkProjects.map(p => p.fqn === projectFqn ? { ...p, is_closed: false, status: 'ACTIVE' } : p),
      }));
    } catch (error: any) {
      const message = error.response?.data?.error || (error instanceof Error ? error.message : 'Failed to reopen project');
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },

  fetchSparkProjectDetails: async (projectFqn: string) => {
    // Set loading state and clear error, but DON'T clear existing data
    set(state => ({
      isLoadingSpark: {
        ...state.isLoadingSpark,
        projectDetails: true
      },
      sparkError: null
    }));

    let project: SparkProjectDetails | null = null; // Keep project temporarily
    try {
      project = await client.getSparkProject(projectFqn);
      if (!project) {
        // Handle case where getSparkProject might return null/undefined if API allows
        throw new Error("Project not found.");
      }
      if (!project._flow_fqn) {
        throw new Error("Project data loaded, but flow FQN is missing.");
      }

      // Also fetch creative types if not already loaded
      if (get().sparkCreativeTypes.length === 0) {
        get().fetchSparkCreativeTypes();
      }

      // Fetch flow, creatives, and jobs in parallel
      // Destructure carefully, checking for null/undefined results individually
      const [flowResult, creativesResult, jobsResult] = await Promise.allSettled([
        client.getSparkFlow(project._flow_fqn),
        client.getSparkCreatives(projectFqn),
        client.getSparkJobs(projectFqn)
      ]);

      const creatives = creativesResult.status === 'fulfilled' ? (creativesResult.value || []) : [];
      const jobs = jobsResult.status === 'fulfilled' ? (jobsResult.value || []) : [];
      // Synthesize per-form definitions from overlay + elements
      const definitionUpdates: Record<string, Record<string, SparkFormDefinition> | null> = {};
      creatives.forEach(creative => {
        definitionUpdates[creative.fqn] = buildSparkFormDefinitions(creative.forms_overlay, creative.elements_definition);
      });
      const mergedDefinitions = {
        ...get().sparkFormDefinitions,
        ...definitionUpdates,
      };

      // Check flow result specifically
      if (flowResult.status === 'rejected' || !flowResult.value) {
        console.error("Flow fetch failed or returned null:", flowResult.status === 'rejected' ? flowResult.reason : 'null value');
        // Set project data but indicate flow is missing
        set(state => {
          // Preserve non-project jobs when updating project context
          const existing = state.currentSparkJobs || [];
          const nonProjectJobs = existing.filter((j: any) => {
            const owners: string[] = Array.isArray(j?._owner_fqns) ? j._owner_fqns : [];
            const hasProjectOwner = owners.some((o: string) => typeof o === 'string' && o.includes('spark.project'));
            return !hasProjectOwner;
          });
          const mergedJobs = [...jobs, ...nonProjectJobs.filter(j => !jobs.find(nj => nj.fqn === (j as any).fqn))];
          return {
            currentSparkProject: project as any,
            currentSparkFlow: null,
            currentSparkCreatives: creatives,
            currentSparkJobs: mergedJobs,
            sparkFormDefinitions: mergedDefinitions,
            sparkError: `Project loaded, but failed to load associated Flow (${(project as any)._flow_fqn}).` // More specific error
          };
        });
        // Optionally re-throw or just return to stop further processing in this action
        // throw new Error(`Failed to load associated Flow (${project.flow_fqn})`);
      } else {
        // All fetches (or at least project and flow) succeeded
        const flow = flowResult.value;

        set(state => {
          const existing = state.currentSparkJobs || [];
          const nonProjectJobs = existing.filter((j: any) => {
            const owners: string[] = Array.isArray(j?._owner_fqns) ? j._owner_fqns : [];
            const hasProjectOwner = owners.some((o: string) => typeof o === 'string' && o.includes('spark.project'));
            return !hasProjectOwner;
          });
          const mergedJobs = [...jobs, ...nonProjectJobs.filter(j => !jobs.find(nj => nj.fqn === (j as any).fqn))];
          return {
            currentSparkProject: project,
            currentSparkFlow: flow,
            currentSparkCreatives: creatives,
            currentSparkJobs: mergedJobs,
            sparkFormDefinitions: mergedDefinitions,
            sparkError: null // Clear any previous error on full success
          };
        });
      }

    } catch (error: any) {
      // This catches errors from getSparkProject or re-thrown errors
      console.error("Error fetching project details:", error);
      // Set error, but keep existing data if possible
      set({ sparkError: error.message || 'Failed to fetch Spark project details' });
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, projectDetails: false } }));
    }
  },

  createSparkProject: async (flowFqn: string, projectName: string): Promise<string | null> => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      const newProject = await client.createSparkProject({
        flow_fqn: flowFqn,
        name: projectName,
      });
      return newProject.fqn;
    } catch (error: any) {
      set({ sparkError: error.message || 'Failed to create Spark project' });
      return null;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },

  deleteSparkProject: async (projectFqn: string): Promise<boolean> => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      const response = await client.deleteSparkProject({ project_fqn: projectFqn });
      if (response.success) {
        // Remove from local state immediately for better UX
        set(state => ({
          sparkProjects: state.sparkProjects.filter(p => p.fqn !== projectFqn)
        }));
        return true;
      }
      return false;
    } catch (error: any) {
      set({ sparkError: error.message || 'Failed to delete Spark project' });
      return false;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },

  fetchSparkFlows: async (): Promise<SparkFlow[]> => {
    // Note: Not setting global loading state for this as it's specific to the modal
    try {
      const flows = await client.getSparkFlows();
      return flows || [];
    } catch (error: any) {
      console.error("Failed to fetch flows in store:", error);
      // Error handled in the modal component that calls this
      throw error; // Re-throw for the component to catch
    }
  },

  updateSparkProjectOverview: async (projectFqn: string, data: { name?: string; briefing?: string; briefing_session_id?: string; tag_pattern?: SparkTagDimensionSelection[] }) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      const updatePayload: { project: Partial<SparkProjectDetails> & { fqn: string; briefing_session_id?: string } } = {
        project: {
          fqn: projectFqn,
        }
      };

      if (data.name !== undefined) {
        updatePayload.project.name = data.name;
      }
      if (data.briefing !== undefined) {
        updatePayload.project.briefing = data.briefing;
      }
      if (data.briefing_session_id !== undefined) {
        // Pass session ID so backend can persist citations from ephemeral index
        updatePayload.project.briefing_session_id = data.briefing_session_id;
      }
      if (data.tag_pattern !== undefined) {
        updatePayload.project.tag_pattern = data.tag_pattern;
      }

      // Call the AtlasClient method
      const updatedProject = await client.updateSparkProject(updatePayload);

      // Update the current project state if it's the one being viewed
      set(state => ({
        currentSparkProject: state.currentSparkProject?.fqn === projectFqn
          ? { ...state.currentSparkProject, ...updatedProject }
          : state.currentSparkProject,
        sparkError: null
      }));

      // Optional: Update the list view if needed (e.g., name changed)
      if (data.name !== undefined) {
        set(state => ({
          sparkProjects: state.sparkProjects.map(p =>
            p.fqn === projectFqn
              ? { ...p, name: data.name as string }
              : p
          )
        }));
      }

    } catch (error: any) {
      console.error("Failed to update project overview:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to update project");
      set({ sparkError: message });
      // Re-throw the error so the calling component can show the error toast
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },

  uploadSparkResource: async (projectFqn: string, file: File): Promise<void> => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, uploadResource: true }, sparkError: null }));
    try {
      const updatedProject = await client.uploadSparkResource(projectFqn, file);
      set(state => ({
        currentSparkProject: state.currentSparkProject?.fqn === projectFqn
          ? updatedProject
          : state.currentSparkProject,
        sparkError: null
      }));
      // Fetch jobs after successful upload
      await get().fetchSparkJobs(projectFqn);
    } catch (error: any) {
      console.error("Failed to upload resource:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to upload resource");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, uploadResource: false } }));
    }
  },

  suggestSparkDeepResearchBriefing: async (projectFqn: string): Promise<string | null> => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, suggestSparkDeepResearchBriefing: true }, sparkError: null }));
    try {
      const response = await client.suggestSparkDeepResearchBriefing(projectFqn);
      return response.briefing;
    } catch (error: any) {
      console.error("Failed to suggest DeepSights Research briefing:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to suggest DeepSights Research briefing");
      set({ sparkError: message });
      // Re-throw for component to handle
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, suggestSparkDeepResearchBriefing: false } }));
    }
  },

  runSparkDeepResearch: async (projectFqn: string, briefing: string, webSearch: boolean): Promise<void> => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, runSparkDeepResearch: true }, sparkError: null }));
    try {
      const updatedProject = await client.runSparkDeepResearch(projectFqn, briefing, webSearch);
      set(state => ({
        currentSparkProject: state.currentSparkProject?.fqn === projectFqn
          ? updatedProject
          : state.currentSparkProject,
        sparkError: null
      }));
      // Fetch jobs after successful run
      await get().fetchSparkJobs(projectFqn);
    } catch (error: any) {
      console.error("Failed to run deep research:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to run desk research");
      set({ sparkError: message });
      // Re-throw for component toast
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, runSparkDeepResearch: false } }));
    }
  },

  generateFSQ: async (projectFqn: string): Promise<FocusSearchQuestion[]> => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, generateFSQ: true }, sparkError: null }));
    try {
      const data = await client.generateFSQ(projectFqn);
      return data.focus_search_questions;
    } catch (error: any) {
      console.error("Failed to generate focus questions:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to generate focus questions");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, generateFSQ: false } }));
    }
  },

  runFSQResearch: async (projectFqn: string, fsqs: FocusSearchQuestion[], webSearch: boolean): Promise<number> => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, runFSQResearch: true }, sparkError: null }));
    try {
      const data = await client.runFSQResearch(projectFqn, fsqs, webSearch);
      set(state => ({
        currentSparkProject: state.currentSparkProject?.fqn === projectFqn
          ? data.project
          : state.currentSparkProject,
        sparkError: null
      }));
      // Fetch jobs after successful run
      await get().fetchSparkJobs(projectFqn);
      return data.jobs_created;
    } catch (error: any) {
      console.error("Failed to run FSQ research:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to start research");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, runFSQResearch: false } }));
    }
  },

  suggestSparkResources: async (projectFqn): Promise<{ suggested_resources: SparkSuggestedResource[] }> => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, suggestResources: true }, sparkError: null }));
    try {
      const data = await client.suggestSparkResources(projectFqn);
      return data;
    } catch (error: any) {
      console.error("Failed to suggest dossiers:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to suggest dossiers");
      set({ sparkError: message });
      throw error; // Re-throw for component to handle
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, suggestResources: false } }));
    }
  },
  addSparkResource: async (projectFqn: string, resourceFqn: string, comment: string | null) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, addResource: true }, sparkError: null }));
    try {
      const updatedProject = await client.addSparkResource({ project_fqn: projectFqn, resource_fqn: resourceFqn, comment: comment });
      // Update the current project state with the response
      set(state => ({
        currentSparkProject: state.currentSparkProject?.fqn === projectFqn
          ? updatedProject
          : state.currentSparkProject,
        sparkError: null
      }));
    } catch (error: any) {
      console.error("Failed to add resource:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to add resource");
      set({ sparkError: message });
      throw error; // Re-throw for component to handle
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, addResource: false } }));
    }
  },
  removeSparkResource: async (projectFqn: string, identifiers: { resourceFqn: string | null, jobFqn: string | null }) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, removeResource: true }, sparkError: null }));
    // Loading toast is handled in the component now
    try {
      // Pass identifiers object to client
      const updatedProject = await client.removeSparkResource(projectFqn, identifiers);
      // Update the current project state optimistically or based on response
      set(state => ({
        currentSparkProject: state.currentSparkProject?.fqn === updatedProject.fqn
          ? updatedProject
          : state.currentSparkProject,
        sparkError: null,
      }));
      // Success toast handled in component
    } catch (error: any) {
      console.error("Failed to remove resource:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to remove resource");
      set({ sparkError: message });
      // Error toast handled in component
      throw error; // Re-throw to allow ProjectSetupPanel to handle its state
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, removeResource: false } }));
    }
  },
  addSparkLabel: async (creativeFqns: string[], label: string) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      await client.addSparkLabel({ creative_fqns: creativeFqns, label: label });
      set(state => ({ sparkError: null }));
    } catch (error: any) {
      console.error("Failed to add label:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to add label");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },
  removeSparkLabel: async (creativeFqns: string[], label: string) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      await client.removeSparkLabel({ creative_fqns: creativeFqns, label: label });
      set(state => ({ sparkError: null }));
    } catch (error: any) {
      console.error("Failed to remove label:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to remove label");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },
  deleteSparkCreatives: async (creativeFqns) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      await client.deleteSparkCreatives({ creative_fqns: creativeFqns });
      set(state => {
        const updatedDefinitions = { ...state.sparkFormDefinitions };
        creativeFqns.forEach(fqn => {
          if (updatedDefinitions.hasOwnProperty(fqn)) {
            delete updatedDefinitions[fqn];
          }
        });
        return { sparkError: null, sparkFormDefinitions: updatedDefinitions };
      });
    } catch (error: any) {
      console.error("Failed to delete creatives:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to delete creatives");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },

  updateSparkCreative: async (creativeFqn, data) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      const payload: { creative: Record<string, unknown> } = {
        creative: { fqn: creativeFqn }
      };

      if (data.name !== undefined) {
        payload.creative.name = data.name;
      }
      if (data.description !== undefined) {
        payload.creative.description = data.description;
      }
      if (data.tag_pattern !== undefined) {
        payload.creative.tag_pattern = data.tag_pattern;
      }

      const updatedCreative = await client.updateSparkCreative(payload as { creative: Partial<SparkCreative> & { fqn: string } });

      set(state => ({
        currentSparkCreatives: state.currentSparkCreatives.map(creative =>
          creative.fqn === creativeFqn ? { ...creative, ...updatedCreative } : creative
        ),
        sparkFormDefinitions: {
          ...state.sparkFormDefinitions,
          [updatedCreative.fqn]: (() => {
            return buildSparkFormDefinitions(updatedCreative.forms_overlay, updatedCreative.elements_definition);
          })(),
        },
        sparkError: null,
      }));
    } catch (error: any) {
      console.error('Failed to update creative:', error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : 'Failed to update creative');
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },
  updateSparkIteration: async (iterationFqn, data) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      const updated = await client.updateSparkIteration({ iteration: { fqn: iterationFqn, ...data } } as any);
      // Synthesize per-form values/issues from flat iteration using the creative's overlay
      set(state => {
        const creative = state.currentSparkCreatives.find(c => c.fqn === (typeof updated._creative_fqn === 'string' ? updated._creative_fqn : String(updated._creative_fqn)));
        const { valuesByForm, issuesByForm } = synthesizePerFormFromOverlay(creative, updated as any);
        return {
          sparkError: null,
          currentSparkCreatives: state.currentSparkCreatives.map(existing =>
            mergeActiveIterationIntoCreative(existing, updated as SparkIteration)
          ),
          sparkIterationFormValues: { ...state.sparkIterationFormValues, [updated.fqn]: valuesByForm },
          sparkIterationValidationIssues: { ...state.sparkIterationValidationIssues, [updated.fqn]: issuesByForm },
        };
      });
      return updated;
    } catch (error: any) {
      console.error("Failed to update iteration:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to update iteration");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },
  addSparkFeedback: async (iterationFqn: string, content: string, metadata?: Record<string, any>, isComment?: boolean) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      // Construct payload including metadata and is_comment if provided
      const payload: { content: string; metadata?: Record<string, any>; is_comment?: boolean } = { content: content };
      if (metadata) {
        payload.metadata = metadata;
      }
      if (isComment !== undefined) {
        payload.is_comment = isComment;
      }
      // Pass the constructed payload to the client method
      await client.addSparkFeedback(iterationFqn, payload);
      set(state => ({ sparkError: null }));
      // OPTIONAL: Optimistic update - Add feedback with metadata to local state immediately
      // set(state => { ... update currentSparkCreatives/currentSparkProject ... });
    } catch (error: any) {
      console.error("Failed to add feedback:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to add feedback");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },
  updateSparkFeedback: async (iterationFqn, feedbackId, content) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      await client.updateSparkFeedback(iterationFqn, feedbackId, { content: content });
      set(state => ({ sparkError: null }));
    } catch (error: any) {
      console.error("Failed to update feedback:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to update feedback");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },
  deleteSparkFeedback: async (iterationFqn, feedbackId) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      await client.deleteSparkFeedback(iterationFqn, feedbackId);
      set(state => ({ sparkError: null }));
    } catch (error: any) {
      console.error("Failed to delete feedback:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to delete feedback");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },
  selectSparkCurrentImage: async (iterationFqn: string, elementId: string, fileIds: string[]) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      const updated = await client.selectSparkCurrentImage(iterationFqn, { element_id: elementId, file_ids: fileIds });
      set(state => {
        const creative = state.currentSparkCreatives.find(c => c.fqn === (typeof updated._creative_fqn === 'string' ? updated._creative_fqn : String(updated._creative_fqn)));
        const { valuesByForm, issuesByForm } = synthesizePerFormFromOverlay(creative, updated as any);
        return {
          sparkError: null,
          currentSparkCreatives: state.currentSparkCreatives.map(existing =>
            mergeActiveIterationIntoCreative(existing, updated as SparkIteration)
          ),
          sparkIterationFormValues: { ...state.sparkIterationFormValues, [updated.fqn]: valuesByForm },
          sparkIterationValidationIssues: { ...state.sparkIterationValidationIssues, [updated.fqn]: issuesByForm },
        };
      });
      return updated;
    } catch (error: any) {
      console.error("Failed to select current image:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to select current image");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },
  deleteSparkImageAlternative: async (iterationFqn, elementId, fileId) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      const updated = await client.deleteSparkImageAlternative(iterationFqn, { element_id: elementId, file_id: fileId });
      set(state => {
        const creative = state.currentSparkCreatives.find(c => c.fqn === (typeof updated._creative_fqn === 'string' ? updated._creative_fqn : String(updated._creative_fqn)));
        const { valuesByForm, issuesByForm } = synthesizePerFormFromOverlay(creative, updated as any);
        return {
          currentSparkCreatives: state.currentSparkCreatives.map(existing =>
            mergeActiveIterationIntoCreative(existing, updated as SparkIteration)
          ),
          sparkIterationFormValues: { ...state.sparkIterationFormValues, [updated.fqn]: valuesByForm },
          sparkIterationValidationIssues: { ...state.sparkIterationValidationIssues, [updated.fqn]: issuesByForm },
          sparkError: null,
        };
      });
      return updated;
    } catch (error: any) {
      console.error("Failed to delete image alternative:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to delete image alternative");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },
  uploadSparkImageAlternative: async (iterationFqn, elementId, file) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      const updated = await client.uploadSparkImageAlternative(iterationFqn, elementId, file);
      set(state => {
        const creative = state.currentSparkCreatives.find(c => c.fqn === (typeof updated._creative_fqn === 'string' ? updated._creative_fqn : String(updated._creative_fqn)));
        const { valuesByForm, issuesByForm } = synthesizePerFormFromOverlay(creative, updated as any);
        return {
          currentSparkCreatives: state.currentSparkCreatives.map(existing =>
            mergeActiveIterationIntoCreative(existing, updated as SparkIteration)
          ),
          sparkIterationFormValues: { ...state.sparkIterationFormValues, [updated.fqn]: valuesByForm },
          sparkIterationValidationIssues: { ...state.sparkIterationValidationIssues, [updated.fqn]: issuesByForm },
        };
      });
      return updated;
    } catch (error: any) {
      console.error("Failed to upload image alternative:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to upload image alternative");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },
  triggerSparkAction: async (
    target: { projectFqn?: string | null; libraryItemFqn?: string | null },
    actionName: string,
    actionArgs: any,
    options?: { sourceTypeFqn?: string | null; targetTypeFqn?: string | null }
  ) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      const newJob = await client.triggerSparkAction({
        project_fqn: target.projectFqn ?? null,
        library_item_fqn: target.libraryItemFqn ?? null,
        action_name: actionName,
        action_args: actionArgs,
      });
      // Optimistically add the new job if returned
      if (newJob) {
        set(state => ({
          currentSparkJobs: [newJob, ...state.currentSparkJobs],
          sparkError: null
        }));
      } else {
        // If job not returned, refetch (fallback)
        if (target.projectFqn) {
          await get().fetchSparkJobs(target.projectFqn);
        }
      }
    } catch (error: any) {
      console.error("Failed to trigger Spark action:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to trigger action");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },
  // publish/unpublish creative removed; use Library Items instead
  /**
   * Mark one or more Spark jobs as seen by the current user.
   */
  markSparkJobAsSeen: async (jobFqns: string[]) => {
    try {
      // Call bulk mark-as-seen endpoint
      const response = await client.markSparkJobAsSeen({ job_fqns: jobFqns });
      if (response.success) {
        // Update the jobs in the state
        set(state => {
          const updatedJobs = state.currentSparkJobs.map(job =>
            jobFqns.includes(job.fqn)
              ? { ...job, _completion_seen_by_current_user: true }
              : job
          );
          // Decrement unread count in sparkProjects for the affected project(s)
          // Only decrement for jobs that were completed/failed/cancelled and previously unseen.
          const jobsByFqn = new Map(state.currentSparkJobs.map(j => [j.fqn, j] as const));
          // Count decrements per project
          const decByProject = new Map<string, number>();
          jobFqns.forEach(fqn => {
            const original = jobsByFqn.get(fqn);
            if (!original) return;
            const isDone = ['completed', 'failed', 'cancelled'].includes(original._status);
            if (isDone && !original._completion_seen_by_current_user && original.project_fqn) {
              const count = decByProject.get(original.project_fqn) || 0;
              decByProject.set(original.project_fqn, count + 1);
            }
          });
          const updatedProjects = state.sparkProjects.map(p => {
            const dec = decByProject.get(p.fqn) || 0;
            if (dec > 0 && typeof p.unread_completed_jobs === 'number') {
              const next = Math.max(0, p.unread_completed_jobs - dec);
              return { ...p, unread_completed_jobs: next };
            }
            return p;
          });
          return { currentSparkJobs: updatedJobs, sparkProjects: updatedProjects };
        });
      }
      return response.success;
    } catch (error: any) {
      console.error("Failed to mark jobs as seen:", error);
      return false;
    }
  },
  cancelSparkJob: async (jobFqn: string) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, cancelJob: true }, sparkError: null }));
    try {
      const updatedJob = await client.cancelSparkJob(jobFqn);
      // Update the job in the state
      set(state => ({
        currentSparkJobs: state.currentSparkJobs.map(job =>
          job.fqn === jobFqn ? updatedJob : job
        ),
        sparkError: null
      }));

      // Automatically mark the job as seen since the user just interacted with it
      await get().markSparkJobAsSeen([jobFqn]);

      return true;
    } catch (error: any) {
      console.error("Failed to cancel job:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to cancel job");
      set({ sparkError: message });
      return false;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, cancelJob: false } }));
    }
  },
  downloadSparkIterationExport: async (iterationFqn, format) => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: true }, sparkError: null }));
    try {
      const blob = await client.downloadSparkIterationExport(iterationFqn, format);
      // Trigger download in browser
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `iteration_${iterationFqn.split('@')[1]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      set(state => ({ sparkError: null }));
    } catch (error: any) {
      console.error("Failed to download iteration export:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to download iteration export");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, action: false } }));
    }
  },

  // User State & Actions
  tenantUsers: [],
  isLoadingTenantUsers: false,
  fetchTenantUsers: async (force = false) => {
    // Check if already loading (always prevent concurrent requests)
    if (get().isLoadingTenantUsers) {
      return;
    }
    // Skip cache check if force is true
    if (!force && get().tenantUsers.length > 0) {
      return; // Do nothing if data already exists and not forcing refresh
    }
    // Only set loading to true if we are actually fetching
    set({ isLoadingTenantUsers: true });
    try {
      const users = await client.listUsers();
      set({ tenantUsers: users, isLoadingTenantUsers: false });
    } catch (error) {
      console.error('Failed to fetch tenant users:', error);
      set({ isLoadingTenantUsers: false }); // Ensure loading is false on error
    }
  },
  addUserToSparkProject: async (projectFqn: string, username: string): Promise<void> => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, addMember: true }, sparkError: null }));
    try {
      const updatedProject = await client.addUserToSparkProject(projectFqn, username);
      set(state => ({
        currentSparkProject: updatedProject,
        sparkError: null
      }));
    } catch (error: any) {
      console.error("Failed to add user to Spark project:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to add user to Spark project");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, addMember: false } }));
    }
  },
  removeUserFromSparkProject: async (projectFqn: string, username: string): Promise<void> => {
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, removeMember: true }, sparkError: null }));
    try {
      const updatedProject = await client.removeUserFromSparkProject(projectFqn, username);
      set(state => ({
        currentSparkProject: updatedProject,
        sparkError: null
      }));
    } catch (error: any) {
      console.error("Failed to remove user from Spark project:", error);
      const message = error.response?.data?.error || (error instanceof Error ? error.message : "Failed to remove user from Spark project");
      set({ sparkError: message });
      throw error;
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, removeMember: false } }));
    }
  },
  fetchSparkJobs: async (projectFqn: string) => {
    // Use the specific 'jobs' loading state if granular control is needed,
    // otherwise, maybe use the general 'action' loader or none if it should be silent.
    // Let's use the jobs loader for clarity.
    set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, jobs: true }, sparkError: null }));
    try {
      const jobs = await client.getSparkJobs(projectFqn);
      // Preserve non-project jobs (e.g., library item jobs) that were optimistically added
      set(state => {
        const existing = state.currentSparkJobs || [];
        const nonProjectJobs = existing.filter((j: any) => {
          const owners: string[] = Array.isArray(j?._owner_fqns) ? j._owner_fqns : [];
          // Heuristic: treat jobs without a spark.project owner as non-project jobs
          const hasProjectOwner = owners.some((o: string) => typeof o === 'string' && o.includes('spark.project'));
          return !hasProjectOwner;
        });
        const dedupMerged = [...jobs, ...nonProjectJobs.filter(j => !jobs.find(nj => nj.fqn === (j as any).fqn))];
        return { currentSparkJobs: dedupMerged };
      });
    } catch (error: any) {
      console.error(`Failed to fetch jobs for project ${projectFqn}:`, error);
      // Potentially set a specific error, but might conflict with other errors.
      // Keep existing sparkError or set a new one?
      // For now, just log the error and don't overwrite a potentially more relevant one.
      // set({ sparkError: `Failed to refresh job list.` });
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, jobs: false } }));
    }
  },
  fetchMySparkJobs: async () => {
    try {
      const myJobs = await client.getMySparkJobs();
      set(state => {
        const existing = state.currentSparkJobs || [];
        // Merge + dedupe by FQN, prefer fresher record from myJobs
        const map = new Map<string, any>();
        for (const j of existing) map.set((j as any).fqn, j);
        for (const j of myJobs) map.set((j as any).fqn, j);
        return { currentSparkJobs: Array.from(map.values()) };
      });
    } catch (error) {
      // Non-fatal; panel will continue showing existing jobs
    }
  },
  setSparkUserInteractionBlockingRefresh: (isBlocking: boolean) => {
    set({ isUserInteractionBlockingRefresh: isBlocking });
    // If interaction has just ended, check if a refresh was pending
    if (!isBlocking) {
      const pendingFqn = get().pendingProjectRefreshFqn;
      if (pendingFqn) {
        console.log(`User interaction ended. Triggering pending refresh for project: ${pendingFqn}`);
        get().fetchSparkProjectDetails(pendingFqn);
        set({ pendingProjectRefreshFqn: null }); // Clear the pending flag
      }
    }
  },
  resetSparkState: () => {
    set({
      // Reset Spark state
      sparkProjects: [],
      currentSparkProject: null,
      currentSparkFlow: null,
      currentSparkCreatives: [],
      currentSparkJobs: [],
      lastActiveSparkProjectFqn: null,
      workerIsOnline: true,
      currentSparkProjectTotalCompletedCount: null,
      sparkCreativeTypes: [],
      sparkFormDefinitions: {},
      sparkIterationFormValues: {},
      sparkIterationValidationIssues: {},
      sparkError: null,
      isUserInteractionBlockingRefresh: false,
      pendingProjectRefreshFqn: null,
      // Reset Prism/Atlas state (prevents stale tenant data)
      dimensions: [],
      elements: [],
      itemElements: [],
      selectedProfile: null,
      selectedVersion: null,
      profiles: [],
      availableContexts: [],
      selectedContext: null,
      elementProfileMap: new Map(),
      dimensionArticles: [],
      currentItemsDimension: null,
    });
  },
  fetchSparkCreativeTypes: async () => {
    set(state => ({
      isLoadingSpark: { ...state.isLoadingSpark, creativeTypes: true },
      sparkError: null
    }));
    try {
      const types = await client.getSparkCreativeTypes();
      set({ sparkCreativeTypes: types || [] });
    } catch (error: any) {
      console.error("Failed to fetch creative types:", error);
      // Don't set global error since this is a supplementary fetch
    } finally {
      set(state => ({ isLoadingSpark: { ...state.isLoadingSpark, creativeTypes: false } }));
    }
  },
  cacheSparkIterations: (iterationsInput) => {
    const iterations = Array.isArray(iterationsInput) ? iterationsInput : [iterationsInput];
    if (iterations.length === 0) {
      return;
    }
    set(state => {
      const valueUpdates: Record<string, Record<string, SparkFormValues> | null> = {};
      const issueUpdates: Record<string, Record<string, SparkFormValidationIssue[]> | null> = {};
      const iterationsByFqn = new Map(iterations.map(iteration => [iteration.fqn, iteration]));
      iterations.forEach(iteration => {
        if ((iteration as any).forms) {
          const forms = (iteration as any).forms as Record<string, any>;
          valueUpdates[iteration.fqn] = Object.fromEntries(Object.entries(forms).map(([fid, data]) => [fid, (data as any).values ?? {}]));
          issueUpdates[iteration.fqn] = Object.fromEntries(Object.entries(forms).map(([fid, data]) => [fid, (data as any).validation_issues ?? []]));
        } else {
          valueUpdates[iteration.fqn] = null;
          issueUpdates[iteration.fqn] = null;
        }
      });
      return {
        currentSparkCreatives: state.currentSparkCreatives.map(creative => {
          const activeIteration = creative._active_iteration_fqn
            ? iterationsByFqn.get(creative._active_iteration_fqn)
            : undefined;
          return activeIteration ? mergeActiveIterationIntoCreative(creative, activeIteration) : creative;
        }),
        sparkIterationFormValues: { ...state.sparkIterationFormValues, ...valueUpdates },
        sparkIterationValidationIssues: { ...state.sparkIterationValidationIssues, ...issueUpdates },
      };
    });
  },

  // (Legacy published) Removed; use library items instead

  // --- Library Items ---
  searchLibraryItems: async (params) => {
    try {
      return await client.searchLibraryItems(params);
    } catch (error) {
      console.error('Failed to search library items:', error);
      throw error;
    }
  },
  fetchLibraryFacets: async (params) => {
    try {
      return await client.fetchLibraryFacets(params);
    } catch (error) {
      console.error('Failed to fetch library facets:', error);
      throw error;
    }
  },
  loadLibrarySearch: async (params) => {
    const include = params.include_drafts ?? get().libraryIncludeDrafts;
    set(state => ({ isLoadingLibrary: { ...state.isLoadingLibrary, search: true }, libraryError: null }));
    try {
      const resp = await client.searchLibraryItems({ ...params, include_drafts: include });
      set({ librarySearchResults: resp });
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Library search failed';
      set({ libraryError: message });
    } finally {
      set(state => ({ isLoadingLibrary: { ...state.isLoadingLibrary, search: false } }));
    }
  },
  loadLibraryFacets: async (params) => {
    const include = params.include_drafts ?? get().libraryIncludeDrafts;
    set(state => ({ isLoadingLibrary: { ...state.isLoadingLibrary, facets: true } }));
    try {
      const { dimensions } = await client.fetchLibraryFacets({ ...params, include_drafts: include });
      set({ libraryFacets: dimensions || [] });
    } catch (error) {
      // non-fatal; keep previous facets
    } finally {
      set(state => ({ isLoadingLibrary: { ...state.isLoadingLibrary, facets: false } }));
    }
  },
  setLibraryIncludeDrafts: (include: boolean) => set({ libraryIncludeDrafts: include }),
  getLibraryItem: async (itemFqn: string) => {
    try {
      return await client.getLibraryItem(itemFqn);
    } catch (error) {
      console.error('Failed to load library item:', error);
      throw error;
    }
  },
  createLibraryItemFromIteration: async (iterationFqn: string) => {
    try {
      return await client.createLibraryItemFromIteration(iterationFqn);
    } catch (error) {
      console.error('Failed to create library item from iteration:', error);
      throw error;
    }
  },
  publishLibraryItem: async (itemFqn: string) => {
    try {
      return await client.publishLibraryItem(itemFqn);
    } catch (error) {
      console.error('Failed to publish library item:', error);
      throw error;
    }
  },
  unpublishLibraryItem: async (itemFqn: string) => {
    try {
      return await client.unpublishLibraryItem(itemFqn);
    } catch (error) {
      console.error('Failed to unpublish library item:', error);
      throw error;
    }
  },
  updateLibraryItem: async (itemFqn: string, payload) => {
    try {
      return await client.updateLibraryItem(itemFqn, payload);
    } catch (error) {
      console.error('Failed to update library item:', error);
      throw error;
    }
  },
  getLibraryHtmlUrl: (itemFqn: string) => client.getLibraryHtmlUrl(itemFqn),
  getLibraryPptxUrl: (itemFqn: string) => client.getLibraryPptxUrl(itemFqn),
  getLibraryExportAssetUrl: (itemFqn: string, exportId: string, filename: string) => client.getLibraryExportAssetUrl(itemFqn, exportId, filename),
  uploadLibraryItemImageAlternative: async (itemFqn: string, elementId: string, file: File) => {
    try {
      return await client.uploadLibraryItemImageAlternative(itemFqn, elementId, file);
    } catch (error) {
      console.error('Failed to upload image alternative for item:', error);
      throw error;
    }
  },
  selectLibraryItemCurrentImage: async (itemFqn: string, data) => {
    try {
      return await client.selectLibraryItemCurrentImage(itemFqn, data);
    } catch (error) {
      console.error('Failed to select current image for item:', error);
      throw error;
    }
  },
  deleteLibraryItemImageAlternative: async (itemFqn: string, data) => {
    try {
      return await client.deleteLibraryItemImageAlternative(itemFqn, data);
    } catch (error) {
      console.error('Failed to delete image alternative for item:', error);
      throw error;
    }
  },
  setLibraryItemImageComment: async (itemFqn: string, elementId: string, fileId: string, comment: string) => {
    try {
      return await client.setLibraryItemImageComment(itemFqn, elementId, fileId, comment);
    } catch (error) {
      console.error('Failed to set image comment for item:', error);
      throw error;
    }
  },
  listLibraryItemAttachments: async (itemFqn: string) => {
    try {
      return await client.listLibraryItemAttachments(itemFqn);
    } catch (error) {
      console.error('Failed to list attachments for item:', error);
      throw error;
    }
  },
  uploadLibraryItemAttachment: async (itemFqn: string, file: File) => {
    try {
      return await client.uploadLibraryItemAttachment(itemFqn, file);
    } catch (error) {
      console.error('Failed to upload attachment for item:', error);
      throw error;
    }
  },
  deleteLibraryItemAttachment: async (itemFqn: string, fileId: string) => {
    try {
      return await client.deleteLibraryItemAttachment(itemFqn, fileId);
    } catch (error) {
      console.error('Failed to remove attachment for item:', error);
      throw error;
    }
  },
  getLibraryItemAttachmentDownloadUrl: (itemFqn: string, fileId: string) => client.getLibraryItemAttachmentDownloadUrl(itemFqn, fileId),
  deleteLibraryItem: async (itemFqn: string) => {
    try {
      return await client.deleteLibraryItem(itemFqn);
    } catch (error) {
      console.error('Failed to delete library item:', error);
      throw error;
    }
  },
}));
